"use client";

import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useToast } from "@/components/ui/toast";
import { PACK_MUST_BE_EVEN_MESSAGE } from "@/lib/host-panel/catalog-pack";
import { landBatch, revertFailed, toListItemBatch } from "@/lib/host-panel/catalog-persistence";
import type { MutationResult } from "@/lib/host-panel/catalog-mutations";
import type { ListedPiece } from "@/lib/host-panel/catalog-types";
import { hostSaveMessage, isPerPieceRefusal, pieceRefusalMessage } from "@/lib/host-panel/save-failure";
import { handledAsExpiredSession } from "@/lib/host-panel/session-expired";
import { BffFailure, clientPatch } from "@/lib/http/client-http";
import type { BackendGiftListItemMutation } from "@/lib/mappers/backend-gift-list-schema";

/**
 * Writing the host's list items (Requirement 5.4) — the one engine, for every screen that
 * changes a list.
 *
 * It lived inline in `CatalogSection` until 2026-10-07, which is why the PDP's own "Adicionar à
 * minha lista" could not exist: the rule is "the same behaviour as Minha Lista", and that
 * behaviour was a hundred and twenty lines trapped in a component the PDP does not render.
 * Nothing here changed in the move — the chain, the coalescing, the rollback and every message
 * are the same, and `CatalogSection` now consumes this.
 *
 * It sits under `components/` and not `lib/host-panel/` on purpose: it reads the toast context,
 * and `lib/host-panel` deliberately imports nothing from `components/`.
 */

/** The host's BFF route for the list's pieces, and the default `itemsEndpoint`. */
export const HOST_LIST_ITEMS_ENDPOINT = "/api/host/list-items";

export type ListItemsWriter = {
  /** What the screen draws: optimistic, already ahead of what the list holds. */
  pieces: readonly ListedPiece[];
  /**
   * Applies a mutation, says what happened, and writes — unless `deferWrites`.
   *
   * The message comes from the mutation itself, so the confirmation can never say something
   * other than what was written.
   */
  commit: (result: MutationResult) => void;
  /** Enqueues the pending difference. Resolves `false` when it failed and already said so. */
  save: () => Promise<boolean>;
  /** Whether the screen is holding choices the list has not received yet. */
  hasUnsavedChanges: () => boolean;
  /**
   * What the list is **known** to hold — readable synchronously, which is what a screen needs
   * right after `await save()` to count what really landed rather than what it hoped for.
   */
  savedPieces: () => readonly ListedPiece[];
  /** Whether a batch is in flight, for a control that wants to say so (`aria-busy`). */
  isSaving: boolean;
};

/**
 * How one link of the write chain ended. `failedIds` are the pieces that did **not** land, so a
 * confirmation can say what happened; `hasLanded` tells a partial write from one that wrote
 * nothing (a refused arrangement, a dead session, no connection).
 */
type WriteOutcome = { ok: boolean; hasLanded: boolean; failedIds: ReadonlySet<string> };

const NOTHING_FAILED: ReadonlySet<string> = new Set();
const WROTE_ALL: WriteOutcome = { ok: true, hasLanded: true, failedIds: NOTHING_FAILED };

export type ListItemsWriterOptions = {
  /** What the list holds when the screen opens; also the first `savedPieces`. */
  initialPieces: readonly ListedPiece[];
  /** Where the batch goes. The creation flow passes its own route. */
  itemsEndpoint?: string;
  /** From `canWriteList`. False makes `commit` a no-op instead of a request. */
  canWrite: boolean;
  /**
   * `true` for the creation's curation: a commit shows and says, and the **step** writes when
   * the person submits (the person's decision of 2026-10-06). Minha Lista and the PDP write at
   * once, because there each change is a change to a list that already exists.
   */
  deferWrites?: boolean;
  /** Published once, with stable identities, so a step's exits can save before they leave. */
  onProvideWriter?: (writer: {
    save: () => Promise<boolean>;
    hasUnsavedChanges: () => boolean;
  }) => void;
};

export function useListItemsWriter({
  initialPieces,
  itemsEndpoint = HOST_LIST_ITEMS_ENDPOINT,
  canWrite,
  deferWrites = false,
  onProvideWriter,
}: ListItemsWriterOptions): ListItemsWriter {
  const { showToast } = useToast();

  const [pieces, setPieces] = useState<readonly ListedPiece[]>(initialPieces);

  /**
   * Persists what a commit changed (Requirement 5.4) — membership and arrangement, as one
   * batch to `PATCH {itemsEndpoint}` (the host's `/api/host/list-items` by default). The screen
   * is optimistic: it already shows the result when the write leaves, and comes back to what it
   * was if the write fails, saying why.
   */
  const persist = useMutation({
    mutationFn: async ({ batch }: { batch: BackendGiftListItemMutation[] }) => {
      const response = await clientPatch<{ ok: true } | { ok: false; code: string }>(itemsEndpoint, {
        items: batch,
      });

      if (!response.ok) throw new BffFailure(response.code);
    },
    /*
     * Both ends are owned by `writeOnce`, which awaits this mutation: on success it moves
     * `saved` forward, and on failure it rolls the screen back to what the list is known to
     * hold and says why. They stay declared, and empty, because a mutation whose outcome nobody
     * answers for is the defect `src/__tests__/mutation-consistency.test.ts` looks for — and here
     * the answer is one `await` away, not in a callback.
     */
    onSuccess: () => undefined,
    onError: () => undefined,
  });

  /**
   * What the list is **known** to hold, which the optimistic screen runs ahead of.
   *
   * Every write is the difference between this and the screen, never the difference one press
   * made: that is what lets presses be coalesced without any of them being lost.
   */
  const saved = useRef<readonly ListedPiece[]>(initialPieces);
  /** The screen's latest, readable from inside a queued write without waiting for a render. */
  const latest = useRef<readonly ListedPiece[]>(initialPieces);
  /**
   * The writes, in a chain — **one request at a time, and the next one carries everything that
   * happened meanwhile**.
   *
   * One request per press is what the curation used to do, and it broke twice over. The BFF cuts
   * writes off at `HOST_WRITE_RATE_LIMIT` per minute **per IP** (`with-api-route.ts`), which
   * choosing twenty pieces reaches at a perfectly normal pace — and an office shares one budget,
   * so one person spent everybody's. And two presses on the same piece raced: add and remove
   * travelled as separate requests, each resolving the list again on the server, so the order they
   * landed in was the network's to decide.
   *
   * Chaining fixes both. Twenty presses enqueue twenty links, but each link writes the net
   * difference against `saved`, so the first carries press one, the second carries presses two to
   * twenty, and the rest find nothing to say. No timer holds a change: there is always either a
   * request in flight or nothing pending, so navigating right after a press cannot drop it —
   * which a debounce would.
   */
  const writeChain = useRef<Promise<WriteOutcome>>(Promise.resolve(WROTE_ALL));

  const hasUnsavedChanges = useCallback(
    () => toListItemBatch(saved.current, latest.current).length > 0,
    [],
  );

  const savedPieces = useCallback((): readonly ListedPiece[] => saved.current, []);

  /**
   * The backend refused a batch for a reason that belongs to one piece, and refused all of it.
   * Each piece is asked again on its own: the ones that go through are written, the ones that do
   * not are put back and told apart, each with its own reason (`pieceRefusalMessage`).
   *
   * A batch of one has nothing to isolate — its refusal is the piece's, and no request is made.
   * Anything that is not a per-piece refusal stops the asking (a session that died, the rate
   * limit): the pieces still waiting fail with it, since asking again would fail the same way.
   */
  const settlePieceByPiece = useCallback(
    async (
      batch: BackendGiftListItemMutation[],
      target: readonly ListedPiece[],
      firstError: unknown,
    ): Promise<WriteOutcome> => {
      const refused = new Map<string, unknown>();
      const landed = new Set<string>();

      if (batch.length === 1) {
        refused.set(batch[0].productId, firstError);
      } else {
        let stoppedBy: unknown = null;

        for (const item of batch) {
          if (stoppedBy !== null) {
            refused.set(item.productId, stoppedBy);
            continue;
          }

          try {
            await persist.mutateAsync({ batch: [item] });
            landed.add(item.productId);
          } catch (error) {
            refused.set(item.productId, error);
            if (!isPerPieceRefusal(error)) stoppedBy = error;
          }
        }
      }

      saved.current = landBatch(saved.current, target, landed);
      const failedIds = new Set(refused.keys());
      // Only the refused pieces go back: what landed stays, and so does any press made meanwhile.
      const reverted = revertFailed(latest.current, saved.current, failedIds);
      latest.current = reverted;
      setPieces(reverted);

      const errors = [...refused.values()];
      // A dead session is an exit, not a message the navigation would wipe (`session-expired.ts`).
      if (errors.some((error) => handledAsExpiredSession(error))) {
        return { ok: false, hasLanded: landed.size > 0, failedIds };
      }

      const removals = new Set(batch.filter((item) => item.remove === true).map((item) => item.productId));
      const nameOf = (id: string) =>
        [...target, ...saved.current].find((piece) => piece.id === id)?.name ?? "Essa peça";
      // One line per distinct reason: twenty pieces refused for the same cause are said once.
      const messages = new Set(
        [...refused].map(([id, error]) =>
          pieceRefusalMessage({ error, isRemoval: removals.has(id), name: nameOf(id) }),
        ),
      );
      for (const message of messages) showToast(message, { variant: "error" });

      return { ok: false, hasLanded: landed.size > 0, failedIds };
    },
    [persist, showToast],
  );

  /** Writes the net difference once, and says how it ended. Failures are already said. */
  const writeOnce = useCallback(async (): Promise<WriteOutcome> => {
    const target = latest.current;

    let batch: BackendGiftListItemMutation[];
    try {
      batch = toListItemBatch(saved.current, target);
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
      showToast(PACK_MUST_BE_EVEN_MESSAGE, { variant: "error" });

      return { ok: false, hasLanded: false, failedIds: NOTHING_FAILED };
    }

    // Already said by an earlier link of the chain.
    if (batch.length === 0) return WROTE_ALL;

    try {
      await persist.mutateAsync({ batch });
      saved.current = target;

      return WROTE_ALL;
    } catch (error) {
      if (isPerPieceRefusal(error)) return settlePieceByPiece(batch, target, error);

      // Back to what the list is known to hold — not to what the screen held one press ago,
      // which on a run of presses would confirm some of them and silently drop the rest.
      setPieces(saved.current);
      latest.current = saved.current;
      const outcome: WriteOutcome = {
        ok: false,
        hasLanded: false,
        failedIds: new Set(batch.map((item) => item.productId)),
      };

      /*
       * A dead session is not a save failure to be reported: it is a session to be ended. The
       * toast would otherwise be the only answer, and the next write would be refused the same
       * way — which in the curation closes a trap (`session-expired.ts`).
       */
      if (handledAsExpiredSession(error)) return outcome;

      showToast(hostSaveMessage(error), { variant: "error" });

      return outcome;
    }
  }, [persist, settlePieceByPiece, showToast]);

  /** Enqueues a write and hands back how it ended, when it is this one's turn and it is done. */
  const enqueue = useCallback((): Promise<WriteOutcome> => {
    writeChain.current = writeChain.current.then(writeOnce);

    return writeChain.current;
  }, [writeOnce]);

  /** Enqueues a write and resolves `false` when it failed in whole or in part (and said so). */
  const save = useCallback((): Promise<boolean> => enqueue().then((outcome) => outcome.ok), [enqueue]);

  useEffect(() => {
    onProvideWriter?.({ save, hasUnsavedChanges });
  }, [onProvideWriter, save, hasUnsavedChanges]);

  const commit = useCallback(
    (result: MutationResult) => {
      // Last gate, after the triggers are already off the screen: a write that reaches here on a
      // list that cannot be changed is a defect, and this makes it a no-op rather than a request.
      if (!canWrite) return;

      if (result.isRejected) {
        if (result.message !== null) showToast(result.message, { variant: "error" });
        return;
      }

      // An arrangement the backend cannot hold is refused before the screen confirms it.
      // Measured against what the list is known to hold, which is what the write will carry.
      try {
        toListItemBatch(saved.current, result.pieces);
      } catch (error) {
        if (!(error instanceof RangeError)) throw error;
        showToast(PACK_MUST_BE_EVEN_MESSAGE, { variant: "error" });
        return;
      }

      setPieces(result.pieces);
      latest.current = result.pieces;

      // The curation writes at its step, so what it says is what the screen now shows.
      if (deferWrites) {
        if (result.message !== null) showToast(result.message, { variant: "success" });
        return;
      }

      /*
       * Everywhere else the confirmation waits for the write: it said "2 peças fora da sua lista"
       * ahead of a save that could then fail, and the person read a success and an error for the
       * same press. It now describes what landed — all of it, or the part of a bulk action that
       * the backend let through.
       */
      void enqueue().then((outcome) => {
        if (result.message === null) return;

        if (outcome.ok) {
          showToast(result.message, { variant: "success" });
        } else if (outcome.hasLanded && result.affectedIds !== undefined && result.messageFor !== undefined) {
          const landed = result.affectedIds.filter((id) => !outcome.failedIds.has(id)).length;
          if (landed > 0) showToast(result.messageFor(landed), { variant: "success" });
        }
      });
    },
    [canWrite, deferWrites, enqueue, showToast],
  );

  return { pieces, commit, save, hasUnsavedChanges, savedPieces, isSaving: persist.isPending };
}
